import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, Mail, Phone, MapPin, Clock, CheckCircle2, Loader2
} from "lucide-react";
import axios from "axios";
import { industries } from "@/data/industries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    industry: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Inquiry submitted successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", company: "", industry: "", message: "" });
    } catch (err) {
      toast.error("Failed to submit inquiry. Please try again.");
      console.error("Contact form error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="page-header-gradient border-b border-slate-100 py-16 md:py-24 relative overflow-hidden">
        <div className="hero-glow absolute inset-0" />
        <div className="dot-grid absolute inset-0 opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Badge
            variant="outline"
            className="mb-4 px-4 py-1.5 text-blue-600 border-blue-200 bg-white/80 font-semibold backdrop-blur-sm"
          >
            Let's Talk
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Book a <span className="gradient-text">Consultation</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Tell us about your business and the workflows you want to automate.
            We'll come back with a custom plan.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <Card className="bg-white border border-slate-200" data-testid="contact-success">
                  <CardContent className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                      Thank you for reaching out!
                    </h2>
                    <p className="text-sm text-slate-600 mb-6">
                      We've received your inquiry and will get back to you within 24 hours 
                      with a custom assessment of your automation opportunities.
                    </p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="border-slate-200"
                      data-testid="submit-another-btn"
                    >
                      Submit Another Inquiry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white border border-slate-200 shadow-card-base card-accent-blue" data-testid="contact-form-card">
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Full Name *
                          </label>
                          <Input
                            data-testid="contact-name-input"
                            placeholder="John Smith"
                            value={formData.name}
                            onChange={(e) => handleChange("name", e.target.value)}
                            className={`h-11 ${errors.name ? "border-red-400 focus:ring-red-100" : "border-slate-200 focus:border-blue-500 focus:ring-blue-100"}`}
                          />
                          {errors.name && (
                            <p className="text-xs text-red-500 mt-1" data-testid="name-error">{errors.name}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Email *
                          </label>
                          <Input
                            data-testid="contact-email-input"
                            type="email"
                            placeholder="john@company.com"
                            value={formData.email}
                            onChange={(e) => handleChange("email", e.target.value)}
                            className={`h-11 ${errors.email ? "border-red-400 focus:ring-red-100" : "border-slate-200 focus:border-blue-500 focus:ring-blue-100"}`}
                          />
                          {errors.email && (
                            <p className="text-xs text-red-500 mt-1" data-testid="email-error">{errors.email}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Phone
                          </label>
                          <Input
                            data-testid="contact-phone-input"
                            placeholder="+91 93816 67300"
                            value={formData.phone}
                            onChange={(e) => handleChange("phone", e.target.value)}
                            className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Company
                          </label>
                          <Input
                            data-testid="contact-company-input"
                            placeholder="Your Company Ltd."
                            value={formData.company}
                            onChange={(e) => handleChange("company", e.target.value)}
                            className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-100"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          Industry
                        </label>
                        <Select
                          value={formData.industry}
                          onValueChange={(val) => handleChange("industry", val)}
                        >
                          <SelectTrigger
                            data-testid="contact-industry-select"
                            className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-100"
                          >
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((ind) => (
                              <SelectItem key={ind.id} value={ind.name}>
                                {ind.name}
                              </SelectItem>
                            ))}
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          How can we help? *
                        </label>
                        <Textarea
                          data-testid="contact-message-input"
                          placeholder="Describe the manual workflows or pain points you'd like to automate..."
                          value={formData.message}
                          onChange={(e) => handleChange("message", e.target.value)}
                          className={`min-h-[120px] ${errors.message ? "border-red-400 focus:ring-red-100" : "border-slate-200 focus:border-blue-500 focus:ring-blue-100"}`}
                        />
                        {errors.message && (
                          <p className="text-xs text-red-500 mt-1" data-testid="message-error">{errors.message}</p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit-btn"
                        className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-md py-3 h-auto font-semibold shadow-sm transition-all hover:-translate-y-0.5"
                        size="lg"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Send Inquiry
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              <Card className="bg-white border border-slate-200 shadow-card-base card-accent-teal" data-testid="contact-info-card">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                    Contact Information
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Email</p>
                        <p className="text-sm text-slate-700">hello@autoshiftai.in</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Phone</p>
                        <p className="text-sm text-slate-700">+91 93816 67300</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Location</p>
                        <p className="text-sm text-slate-700">Hyderabad, India</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Response Time</p>
                        <p className="text-sm text-slate-700">Within 24 hours</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-teal-50/40 border border-blue-100 shadow-card-base" data-testid="free-consultation-card">
                <CardContent className="p-6">
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-2">
                    What happens next?
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "We review your inquiry within 24 hours",
                      "Schedule a free 30-min discovery call",
                      "Deliver a custom automation assessment",
                      "Propose a solution with clear ROI metrics",
                    ].map((step, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center shrink-0 mt-0.5 font-medium">
                          {idx + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
